// Typed write-side and realtime hooks for the Manage resources.
//
// Every shape here describes JSON as it actually arrives in the browser (Rust
// i64/u64 values are JSON numbers). A generated type that carries a bigint is
// read through its coerced form in `types.ts`, never from `types.gen.ts`
// (`type-source.guard.test.ts`).

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from './client';
import { ApiError } from './errors';
import { useRegisteredMutation } from './mutation';
// Prefixes read off the resources, never re-typed: a key rename must not
// silently turn an invalidation into a no-op, which is exactly what a literal
// copy of the key does.
import { audienceById, audienceEstimateSize, audiencePlannerBenchmarks, audiencePlannerPreview, audiencePreview, audienceSyncStatus, audiencesList, campaignAdSets, campaignById, campaignCreativeAdditions, campaignFlightExtensions, campaignForecast, campaignPlan, campaignPreflight, campaignSyncStatus, campaignsList, creativeById, creativePreviewStatus, creativeReviewPackages, creativeRevisions, creativesList, metaCreativePreviews, metaDeliveryContract, reportingQuery, reportingQueryAllPages } from './resources';
import { CADENCE } from './resource';
import { audienceSyncShouldPoll } from '@/lib/audience-refresh-view';
import { calendarDayRange } from '@/lib/datetime';
import { channelBenchmarkRequest, channelBenchmarkWindow, channelBenchmarks } from '@/lib/channel-benchmarks';
import type {
  Audience,
  AudienceResponse,
  AudienceType,
  Campaign,
  CampaignResponse,
  CampaignStatus,
  Channel,
  ExtendCampaignFlightRequest,
  ExtendCampaignFlightResponse,
  Creative,
  CreativeContentWriteResponse,
  CreativeResponse,
  MediaType,
  RuleGroup,
  SyncStatus,
  ReportQueryRequest,
} from './types';
import type {
  AddCampaignCreativeRequest,
  MetaDeliveryContract,
  MetaPreviewBundle,
  CampaignCreativeAddition,
  CreateReviewPackageRequest,
  CreativeReviewLink,
  CreativeReviewPackageSummary,
  CreativeReviewDecision,
  CreativeRolloutRequest,
  CreativeRolloutResponse,
  SaveCreativeContentRequest,
  MetaPreviewPlacement,
  Projection,
  UploadPart,
  CreativeSlotChangeResponse,
} from './types.gen';

// Re-exported so every existing `from './manage-hooks'` type import keeps
// working; the declarations themselves now live one layer down.
export type {
  ManageAdSet,
  ManageCampaignPlan,
  AudiencePreview,
  AudiencePlannerPreviewInput,
  AudiencePlannerBenchmarks,
  AudienceSyncStatus,
  MetaCreativeSync,
  TtdCreativeSync,
  CreativeUploadInit,
  CreativeUploadComplete,
  SyncPollOptions,
  CampaignFlightInput,
  CampaignCreateInput,
  CampaignAudienceSegmentInput,
  CampaignPlanMutationResponse,
  CampaignPlanResponse,
  CampaignUpdateInput,
  CampaignPreflightCheckStatus,
  CampaignPreflightCheck,
  CampaignPreflight,
  CampaignPatchInput,
  CampaignProviderState,
  CampaignSyncBlock,
  CampaignSyncStatus,
  CampaignSyncTarget,
  CampaignPublishOutcome,
  CampaignForecastInput,
  CampaignForecastAssumptions,
  CampaignForecastProjection,
  CampaignForecast,
  CampaignAction,
  AudienceCreateInput,
  AudiencePatchInput,
  AudiencePreviewInput,
  AudienceEstimateSize,
  CreativeCreateInput,
  CreativePatchInput,
  AttachCreativeAssetInput,
  CreativePreviewStatus,
  CreativeSyncFailureCode,
  CampaignRetrySyncResponse,
  CreativeRetrySyncResponse,
  CreativeUploadInitInput,
  CreativeUploadCompleteInput,
} from './manage-types';
import type {
  ManageAdSet,
  ManageCampaignPlan,
  AudiencePreview,
  AudiencePlannerPreviewInput,
  AudiencePlannerBenchmarks,
  AudienceSyncStatus,
  MetaCreativeSync,
  TtdCreativeSync,
  CreativeUploadInit,
  CreativeUploadComplete,
  SyncPollOptions,
  CampaignFlightInput,
  CampaignCreateInput,
  CampaignAudienceSegmentInput,
  CampaignPlanMutationResponse,
  CampaignPlanResponse,
  CampaignUpdateInput,
  CampaignPreflightCheckStatus,
  CampaignPreflightCheck,
  CampaignPreflight,
  CampaignPatchInput,
  CampaignProviderState,
  CampaignSyncBlock,
  CampaignSyncStatus,
  CampaignSyncTarget,
  CampaignPublishOutcome,
  CampaignForecastInput,
  CampaignForecastAssumptions,
  CampaignForecastProjection,
  CampaignForecast,
  CampaignAction,
  AudienceCreateInput,
  AudiencePatchInput,
  AudiencePreviewInput,
  AudienceEstimateSize,
  CreativeCreateInput,
  CreativePatchInput,
  AttachCreativeAssetInput,
  CreativePreviewStatus,
  CreativeSyncFailureCode,
  CampaignRetrySyncResponse,
  CreativeRetrySyncResponse,
  CreativeUploadInitInput,
  CreativeUploadCompleteInput,
} from './manage-types';


const CAMPAIGN_LIST_PREFIX = campaignsList.all;
const AUDIENCE_LIST_PREFIX = audiencesList.all;
const AUDIENCE_PREVIEW_PREFIX = audiencePreview.all;
const AUDIENCE_PLANNER_PREVIEW_PREFIX = audiencePlannerPreview.all;
const AUDIENCE_ESTIMATE_PREFIX = audienceEstimateSize.all;
const CREATIVE_LIST_PREFIX = creativesList.all;

export const MANAGE_SYNC_POLL_MS = 5_000;


function invalidate(qc: QueryClient, queryKey: readonly unknown[]): void {
  void qc.invalidateQueries({ queryKey });
}

function cacheCampaign(qc: QueryClient, campaign: Campaign): void {
  qc.setQueryData<CampaignResponse>(
    campaignById.key(campaign.id),
    { campaign },
  );
  invalidate(qc, CAMPAIGN_LIST_PREFIX);
}

export function cacheCampaignPlan(qc: QueryClient, plan: ManageCampaignPlan): void {
  cacheCampaign(qc, plan.campaign);
  qc.setQueryData<{ plan: ManageCampaignPlan }>(
    campaignPlan.key(plan.campaign.id),
    { plan },
  );
  invalidate(qc, campaignPreflight.key(plan.campaign.id));
}

function cacheAudience(qc: QueryClient, audience: Audience): void {
  qc.setQueryData<AudienceResponse>(
    audienceById.key(audience.id),
    { audience },
  );
  invalidate(qc, AUDIENCE_LIST_PREFIX);
}

function invalidateAudienceDerived(qc: QueryClient): void {
  invalidate(qc, AUDIENCE_PREVIEW_PREFIX);
  invalidate(qc, AUDIENCE_PLANNER_PREVIEW_PREFIX);
  invalidate(qc, AUDIENCE_ESTIMATE_PREFIX);
}

function cacheCreative(qc: QueryClient, creative: Creative): void {
  qc.setQueryData<CreativeResponse>(
    creativeById.key(creative.id),
    { creative },
  );
  invalidate(qc, CREATIVE_LIST_PREFIX);
}

function encoded(id: string): string {
  return encodeURIComponent(id);
}

// ── Campaigns ─────────────────────────────────────────────────────────────



















export function useCreateCampaign() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: (body: CampaignCreateInput) =>
      api.post<CampaignPlanMutationResponse>('/campaigns', { body })
        .then((r) => r.plan),
    onSuccess: (plan) => cacheCampaignPlan(qc, plan),
  });
}

export function useCampaignPlan(id: string) {
  return useQuery(campaignPlan(id, { enabled: !!id }));
}

export function useUpdateCampaignPlan() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, plan }: { id: string; plan: CampaignUpdateInput }) =>
      api.put<CampaignPlanMutationResponse>(
        `/campaigns/${encoded(id)}`,
        { body: plan },
      ).then((response) => response.plan),
    onSuccess: (plan) => cacheCampaignPlan(qc, plan),
  });
}

export function useDuplicateCampaign() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({
      id,
      newId,
      name,
    }: {
      id: string;
      newId: string;
      name?: string;
    }) =>
      api.post<CampaignPlanMutationResponse>(
        `/campaigns/${encoded(id)}/duplicate`,
        { body: { id: newId, name } },
      ).then((response) => response.plan),
    onSuccess: (plan) => cacheCampaignPlan(qc, plan),
  });
}

/**
 * Preflight, polled slowly until the campaign is ready, then not at all.
 *
 * The cadence is `ambient` (60s) rather than `fast` on purpose. `build_preflight`
 * makes no provider calls, but it runs roughly a dozen D1 queries across
 * productions, audiences, revisions, creatives and delivery groups — and the
 * things it reports move on the order of *minutes*, because what it is usually
 * waiting for is Meta matching a million records. Polling it every five seconds
 * would be twelve times the database traffic for the same answer.
 *
 * It stops itself once `ready` is true: a campaign that has cleared preflight
 * cannot un-clear it without a mutation, and the four mutations that could all
 * invalidate this key already.
 */
export function useCampaignPreflight(id: string, enabled = true) {
  return useQuery(
    campaignPreflight(id, {
      enabled: !!id && enabled,
      cadence: (data) => (data?.ready ? false : CADENCE.ambient),
    }),
  );
}

export function useMetaDeliveryContract(enabled = true) {
  return useQuery(metaDeliveryContract(undefined, { enabled }));
}

export function useMetaCreativePreviews(id: string, enabled = true) {
  return useQuery(metaCreativePreviews(id, { enabled: !!id && enabled }));
}

export function useRefreshMetaCreativePreviews() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (id: string) =>
      api.post<void>(`/creatives/${encoded(id)}/meta-previews/refresh`),
    onSuccess: (_response, id) => {
      invalidate(qc, metaCreativePreviews.key(id));
    },
  });
}

// The legacy share-link mutations lived here and are deleted, not deprecated.
//
// No screen or route referenced either one: the UI mints only verified package
// links (`#p=`), so nothing in the app has been able to create a pre-verification
// `#<token>` link since that flow landed. Confirmed against both environments —
// `creative_review_sessions` holds ZERO rows in prod and in dev, as do all six
// sibling review tables, so this is code nothing called producing data that never
// existed.
//
// The SERVER side is deliberately untouched. `POST /creatives/{id}/review-sessions`
// and `POST /public/creative-reviews/resolve` are still mounted, and
// `resources/public.ts::publicReview` still reads the second one. Removing a
// caller is not the same decision as removing a capability, and the emptiness
// here is evidence of a feature never switched on — not of one retired.

/**
 * One review link per creative. Asking for links to N creatives answers N
 * links, in request order: a creative that already has a live link at its
 * current content gets that link back (`reused`), any other gets a new one that
 * lives 72 hours. There is no revoke; a link ends at its expiry.
 */
export function useCreateCreativeReviewPackage() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (input: CreateReviewPackageRequest) =>
      api.post<CreativeReviewLink[]>('/creatives/review-packages', { body: input }),
    onSuccess: (_result, input) => {
      for (const id of input.creative_ids) {
        void qc.invalidateQueries({ queryKey: creativeReviewPackages.key(id) });
      }
    },
  });
}

/**
 * Every link that ever covered a creative, each with the decisions recorded
 * against it and who opened it. Expired links are included — the approval
 * record attached to a dead link is exactly the thing worth keeping — and
 * `live` says which ones reviewers can still open; a live link minted with a
 * derived token carries its `share_url` again, so it can be copied.
 */
export function useCreativeReviewPackages(creativeId: string | null | undefined) {
  return useQuery(creativeReviewPackages(creativeId ?? '', { enabled: !!creativeId }));
}

export function useReviewMetaCreativePreview() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (input: {
      creativeId: string;
      placement: MetaPreviewPlacement;
      decision: CreativeReviewDecision;
      comment?: string | null;
    }) =>
      api.post<void>(
        `/creatives/${encoded(input.creativeId)}/meta-previews/decisions`,
        {
          body: {
            placement: input.placement,
            decision: input.decision,
            comment: input.comment ?? null,
          },
        },
      ),
    // The bundle carries reviewed_placements and internal_feedback, so a decision
    // that does not invalidate it leaves the gallery rendering stale review state.
    onSuccess: (_response, input) => {
      invalidate(qc, metaCreativePreviews.key(input.creativeId));
    },
  });
}

export function usePatchCampaign() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, patch }: { id: string; patch: CampaignPatchInput }) =>
      api.patch<CampaignResponse>(`/campaigns/${encoded(id)}`, { body: patch })
        .then((r) => r.campaign),
    onSuccess: (campaign) => cacheCampaign(qc, campaign),
  });
}


function useCampaignAction(action: CampaignAction) {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: (id: string) =>
      api.post<CampaignResponse>(`/campaigns/${encoded(id)}/${action}`)
        .then((r) => r.campaign),
    onSuccess: (campaign) => {
      cacheCampaign(qc, campaign);
      invalidate(qc, campaignPlan.key(campaign.id));
      invalidate(qc, campaignPreflight.key(campaign.id));
      void qc.invalidateQueries({
        queryKey: campaignSyncStatus.key(campaign.id),
      });
    },
  });
}

/**
 * A launch or an arm names the plan revision its author reviewed (`expected_revision`), so
 * the API refuses it with a 409 when the draft moved on since, say through an API key's edit,
 * instead of launching a revision nobody looked at (docs/plans/claude-connector.md, Astra
 * review of commit 3).
 */
export interface CampaignLaunchInput {
  id: string;
  /** The plan revision on screen when the operator chose to launch. */
  expectedRevision: number;
}

/** The API's words for a launch or an arm whose reviewed revision is no longer the draft's. */
const LAUNCH_REVISION_CHANGED = 'campaign changed since you reviewed it';

/** A launch or an arm refused because the draft changed after it was reviewed. */
export function isLaunchRevisionConflict(error: unknown): boolean {
  return error instanceof ApiError
    && error.status === 409
    && error.code === 'conflict'
    && error.message.includes(LAUNCH_REVISION_CHANGED);
}

/** What the operator reads after that refusal, in place of the API's sentence. */
export const LAUNCH_REVISION_CHANGED_MESSAGE =
  'This campaign changed after you reviewed it, so nothing was launched. '
  + 'Reload the page to review the latest version, then launch again.';

/** The draft moved on: read the plan again so every screen shows the revision that is there. */
function refreshAfterLaunchConflict(qc: QueryClient, error: unknown, id: string) {
  if (!isLaunchRevisionConflict(error)) return;
  invalidate(qc, campaignPlan.key(id));
  invalidate(qc, campaignById.key(id));
  invalidate(qc, campaignPreflight.key(id));
}

export function useLaunchCampaign() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, expectedRevision }: CampaignLaunchInput) =>
      api.post<CampaignResponse>(`/campaigns/${encoded(id)}/launch`, {
        body: { expected_revision: expectedRevision },
      }).then((r) => r.campaign),
    onSuccess: (campaign) => {
      cacheCampaign(qc, campaign);
      invalidate(qc, campaignPlan.key(campaign.id));
      invalidate(qc, campaignPreflight.key(campaign.id));
      void qc.invalidateQueries({
        queryKey: campaignSyncStatus.key(campaign.id),
      });
    },
    onError: (error, input) => refreshAfterLaunchConflict(qc, error, input.id),
  });
}

/** Arm "launch when ready": the backend performs the real launch once
 *  preflight turns ready; the campaign stays a draft while armed. It arms only the revision
 *  the operator reviewed. */
export function useLaunchWhenReady() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, expectedRevision }: CampaignLaunchInput) =>
      api.post<CampaignResponse>(`/campaigns/${encoded(id)}/launch-when-ready`, {
        body: { expected_revision: expectedRevision },
      }).then((r) => r.campaign),
    onError: (error, input) => refreshAfterLaunchConflict(qc, error, input.id),
    onSuccess: (campaign) => {
      cacheCampaign(qc, campaign);
      invalidate(qc, campaignPreflight.key(campaign.id));
      // The campaign EDIT screen renders from the plan query, not from `byId` — they are
      // sibling keys. Without this the armed chip (and the disarm control inside it) did
      // not appear until the plan's 15-minute staleTime expired.
      invalidate(qc, campaignPlan.key(campaign.id));
      invalidate(qc, CAMPAIGN_LIST_PREFIX);
    },
  });
}

export function useCancelLaunchWhenReady() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: (id: string) =>
      api.delete<void>(`/campaigns/${encoded(id)}/launch-when-ready`).then(() => id),
    onSuccess: (id) => {
      invalidate(qc, campaignById.key(id));
      // Same reason as arming: the screen holding the disarm button reads the plan.
      invalidate(qc, campaignPlan.key(id));
      invalidate(qc, campaignPreflight.key(id));
      invalidate(qc, CAMPAIGN_LIST_PREFIX);
    },
  });
}

export function usePauseCampaign() {
  return useCampaignAction('pause');
}

export function useResumeCampaign() {
  return useCampaignAction('resume');
}

export function useArchiveCampaign() {
  return useCampaignAction('archive');
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: (id: string) =>
      api.delete<void>(`/campaigns/${encoded(id)}`).then(() => id),
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: campaignById.key(id), exact: true });
      invalidate(qc, CAMPAIGN_LIST_PREFIX);
    },
  });
}

export function useCampaignForecast(
  request: CampaignForecastInput | null,
  enabled = true,
) {
  return useQuery(campaignForecast(request, { enabled: enabled && request !== null }));
}

// Re-exported so existing callers keep their import path. The definitions live
// in `lib/campaign-sync-outcome` because the publish sequence is a pure module
// and cannot import this file without pulling react-query in behind it.
export {
  campaignPublishOutcome,
  campaignSyncSettled,
  campaignSyncTerminal,
  terminalSyncBlock,
} from '@/lib/campaign-sync-outcome';
import { campaignLaterLaneInFlight, campaignSyncSettled, campaignSyncTerminal } from '@/lib/campaign-sync-outcome';

export function useCampaignSyncStatus(
  id: string,
  options: SyncPollOptions = {},
) {
  const intervalMs = options.intervalMs ?? CADENCE.fast;
  // Terminality depends on which provider the caller is watching, so the
  // cadence is decided here rather than on the resource.
  //
  // Three tiers, not two. Stopping at `campaignSyncTerminal` is what left the
  // right column's Serving step frozen on "first impressions not counted yet"
  // for the life of the tab: the publish was terminal, so the poll stopped,
  // and the per-minute reconciler's eventual `active` was never fetched.
  // A campaign that is terminal but not settled keeps an ambient poll, which
  // matches the reconciler's own cadence rather than racing it at 5s.
  return useQuery(campaignSyncStatus(id, {
    enabled: !!id && (options.enabled ?? true),
    cadence: (status) => {
      if (!status) return intervalMs;
      // A slot change or roll-out writing its journal: the card's per-audience
      // steps come from these rows, so read them at the urgent cadence. (While
      // the row is still `queued` there are no stage rows yet; the plan's own
      // poll is what re-reads at 2 s then, and this one joins at the first
      // `running` row.) The override replaces the resource cadence entirely,
      // so the predicate is repeated here.
      if (campaignLaterLaneInFlight(status)) return CADENCE.urgent;
      if (campaignSyncSettled(status, options.target)) return CADENCE.ambient;
      return campaignSyncTerminal(status, options.target)
        ? CADENCE.ambient
        : intervalMs;
    },
  }));
}

export function useCampaignAdSets(id: string) {
  return useQuery(campaignAdSets(id, { enabled: !!id }));
}

// The create / patch / delete flight mutations are gone with their routes:
// a campaign's budget must equal the sum of its flights, so every one of them
// could only ever validate at a zero budget. Flights are written through
// `useUpdateCampaignPlan`, which replaces the plan and its flights together.

// ── Audiences ─────────────────────────────────────────────────────────────





export function useCreateAudience() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['audiences'],
    mutationFn: (body: AudienceCreateInput) =>
      api.post<AudienceResponse>('/audiences', { body }).then((r) => r.audience),
    onSuccess: (audience) => {
      cacheAudience(qc, audience);
      invalidateAudienceDerived(qc);
      void qc.invalidateQueries({
        queryKey: audienceSyncStatus.key(audience.id),
      });
    },
  });
}

export function usePatchAudience() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['audiences'],
    mutationFn: ({ id, patch }: { id: string; patch: AudiencePatchInput }) =>
      api.patch<AudienceResponse>(`/audiences/${encoded(id)}`, { body: patch })
        .then((r) => r.audience),
    onSuccess: (audience) => {
      cacheAudience(qc, audience);
      invalidateAudienceDerived(qc);
      void qc.invalidateQueries({
        queryKey: audienceSyncStatus.key(audience.id),
      });
    },
  });
}

function useAudienceResourceAction(action: 'refresh' | 'refresh-size') {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['audiences'],
    mutationFn: (id: string) =>
      api.post<AudienceResponse>(`/audiences/${encoded(id)}/${action}`)
        .then((r) => r.audience),
    onSuccess: (audience) => {
      cacheAudience(qc, audience);
      invalidateAudienceDerived(qc);
      void qc.invalidateQueries({
        queryKey: audienceSyncStatus.key(audience.id),
      });
    },
  });
}

export function useRefreshAudience() {
  return useAudienceResourceAction('refresh');
}

export function useRefreshAudienceSize() {
  return useAudienceResourceAction('refresh-size');
}

export function useArchiveAudience() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['audiences'],
    mutationFn: (id: string) =>
      api.post<void>(`/audiences/${encoded(id)}/archive`).then(() => id),
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: audienceById.key(id), exact: true });
      qc.removeQueries({ queryKey: audienceSyncStatus.key(id), exact: true });
      invalidate(qc, AUDIENCE_LIST_PREFIX);
    },
  });
}

export function useRestoreAudience() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['audiences'],
    mutationFn: (id: string) =>
      api.post<AudienceResponse>(`/audiences/${encoded(id)}/restore`)
        .then((r) => r.audience),
    onSuccess: (audience) => {
      cacheAudience(qc, audience);
      void qc.invalidateQueries({
        queryKey: audienceSyncStatus.key(audience.id),
      });
    },
  });
}

export function useAudiencePreview(
  request: AudiencePreviewInput | null,
  enabled = true,
  // The lookalike fit-pending poll: the builder passes a cadence so a preview
  // of an unfitted seed refetches until the accelerated fit publishes, then
  // stops. Undefined = one-shot. Named `cadence` because that is what it is —
  // it goes straight into the resource's `CallOverrides`, and calling it
  // `refetchInterval` implied a raw TanStack option this layer no longer sets.
  cadence?: (data: AudiencePreview | undefined) => number | false,
) {
  return useQuery(audiencePreview(request, {
    enabled: enabled && request !== null,
    cadence,
  }));
}

export function useAudiencePlannerPreview(
  request: AudiencePlannerPreviewInput | null,
  enabled = true,
) {
  return useQuery(audiencePlannerPreview(request, { enabled: enabled && request !== null }));
}

/**
 * Daily buyer-composition reference profiles. This read is deliberately
 * separate from the live Planner POST: changing the comparison production
 * must never recompute the selected audience.
 */
export function useAudiencePlannerBenchmarks(
  productionId: string | null,
  enabled = true,
) {
  return useQuery(audiencePlannerBenchmarks(productionId, { enabled }));
}

export function useAudienceEstimateSize(
  request: AudiencePreviewInput | null,
  enabled = true,
) {
  return useQuery(audienceEstimateSize(request, { enabled: enabled && request !== null }));
}

export function useAudienceSyncStatus(
  id: string,
  options: SyncPollOptions = {},
) {
  const intervalMs = options.intervalMs ?? CADENCE.fast;
  return useQuery(audienceSyncStatus(id, {
    enabled: !!id && (options.enabled ?? true),
    // Not `status === 'synced'`. Pressing Refresh writes `next_refresh_at` and
    // leaves both legs synced, so a status-keyed cadence switches the poll off
    // at precisely the moment a pickup is pending — and the drawer would have
    // reported "Refresh scheduled" for the life of the tab.
    cadence: (data) => (audienceSyncShouldPoll(data, Date.now()) ? intervalMs : false),
  }));
}

// ── Creatives ─────────────────────────────────────────────────────────────









export function useCreateCreative() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (body: CreativeCreateInput) =>
      api.post<CreativeResponse>('/creatives', { body }).then((r) => r.creative),
    onSuccess: (creative) => cacheCreative(qc, creative),
  });
}

/**
 * Content (copy, destination) is immutable per revision on the server: a
 * PATCH of an approved or pinned revision answers with the creative at the
 * NEXT revision and names it in `created_revision`; name and agency land in
 * place. Callers that only care about the creative read `.creative`.
 */
export function usePatchCreative() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: ({ id, patch }: { id: string; patch: CreativePatchInput }) =>
      api.patch<CreativeContentWriteResponse>(`/creatives/${encoded(id)}`, { body: patch }),
    onSuccess: ({ creative }) => {
      cacheCreative(qc, creative);
      // A content edit re-stages the official previews for that revision, and
      // a minted revision is a new row in the archive.
      invalidate(qc, metaCreativePreviews.key(creative.id));
      invalidate(qc, creativePreviewStatus.key(creative.id));
      invalidate(qc, creativeRevisions.key(creative.id));
    },
  });
}

/** Every revision of one creative — the archive behind the drawer's Versions tab. */
export function useCreativeRevisions(id: string, options: { enabled?: boolean } = {}) {
  return useQuery(creativeRevisions(id, { enabled: !!id && (options.enabled ?? true) }));
}

export function useDeleteCreative() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (id: string) =>
      api.delete<void>(`/creatives/${encoded(id)}`).then(() => id),
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: creativeById.key(id), exact: true });
      invalidate(qc, CREATIVE_LIST_PREFIX);
    },
  });
}

export function useAttachCreativeAsset() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: ({ creativeId, ...body }: AttachCreativeAssetInput) =>
      api.post<CreativeContentWriteResponse>(
        `/creatives/${encoded(creativeId)}/assets`,
        { body },
      ).then((r) => r.creative),
    onSuccess: (creative) => {
      cacheCreative(qc, creative);
      invalidate(qc, creativeRevisions.key(creative.id));
    },
  });
}

function useCreativeAction(action: 'submit-for-review' | 'approve' | 'archive' | 'restore') {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (id: string) =>
      api.post<CreativeResponse>(`/creatives/${encoded(id)}/${action}`)
        .then((r) => r.creative),
    onSuccess: (creative) => {
      cacheCreative(qc, creative);
      void qc.invalidateQueries({
        queryKey: creativePreviewStatus.key(creative.id),
      });
      invalidate(qc, creativeRevisions.key(creative.id));
    },
  });
}

export function useSubmitCreativeForReview() {
  return useCreativeAction('submit-for-review');
}

export function useApproveCreative() {
  return useCreativeAction('approve');
}

export function useArchiveCreative() {
  return useCreativeAction('archive');
}

export function useRestoreCreative() {
  return useCreativeAction('restore');
}

export function creativePreviewPollInterval(
  status: CreativePreviewStatus | undefined,
  intervalMs: number,
): number | false {
  return status?.terminal ? false : intervalMs;
}

export function useCreativePreviewStatus(
  id: string,
  options: SyncPollOptions = {},
) {
  const intervalMs = options.intervalMs ?? CADENCE.fast;
  return useQuery(creativePreviewStatus(id, {
    enabled: !!id && (options.enabled ?? true),
    cadence: (data) => creativePreviewPollInterval(data, intervalMs),
  }));
}

/// Re-drive a campaign's provider sync after the cron lane exhausted its ten
/// attempts (a TTD gateway window can do that): resets the attempt count and
/// enqueues the upsert the save path would.
export function useRetryCampaignSync() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: (id: string) =>
      api.post<CampaignRetrySyncResponse>(`/campaigns/${encoded(id)}/retry-sync`),
    onSuccess: (_result, id) => {
      invalidate(qc, campaignSyncStatus.key(id));
      invalidate(qc, campaignById.key(id));
      invalidate(qc, campaignsList.all);
    },
  });
}

/// Roll a newer approved creative revision out to one slot of a published
/// campaign (0024). The route answers 202 with the queued rollout; the plan
/// carries it from then on and refetches as the queue writes its verdict.
export function useRolloutCreative() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns', 'creatives'],
    mutationFn: ({
      campaignId,
      position,
      ...body
    }: CreativeRolloutRequest & { campaignId: string; position: number }) =>
      api
        .post<CreativeRolloutResponse>(
          `/campaigns/${encoded(campaignId)}/creatives/${position}/rollout`,
          { body },
        )
        .then((r) => r.rollout),
    onSuccess: (rollout) => {
      invalidate(qc, campaignPlan.key(rollout.campaign_id));
      invalidate(qc, campaignSyncStatus.key(rollout.campaign_id));
      invalidate(qc, creativeRevisions.key(rollout.creative_id));
      invalidate(qc, creativesList.all);
    },
  });
}

export function useRetryCreativeSync() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: (id: string) =>
      api.post<CreativeRetrySyncResponse>(
        `/creatives/${encoded(id)}/retry-sync`,
      ),
    // `creativePreviewStatus` alone was not enough, and the gap was invisible:
    // it is the `realtime` resource that polls itself anyway, while the screens
    // that render a creative's sync state read `c.sync` out of the authoritative
    // creatives list. Retrying from the library drawer therefore changed
    // nothing on screen — the panel kept saying `failed` and offering Retry
    // long after the creative had published, which is the opposite of what had
    // happened. Invalidate every resource that carries the state this mutation
    // just changed.
    onSuccess: (_result, id) => {
      invalidate(qc, creativePreviewStatus.key(id));
      invalidate(qc, creativeById.key(id));
      invalidate(qc, creativesList.all);
      invalidate(qc, creativeRevisions.key(id));
    },
  });
}

export function useInitCreativeUpload() {
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: ({ creativeId, ...body }: CreativeUploadInitInput) =>
      api.post<CreativeUploadInit>(
        `/creatives/${encoded(creativeId)}/upload-init`,
        { body },
      ),
  });
}

export function useCompleteCreativeUpload() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: ({ creativeId, ...body }: CreativeUploadCompleteInput) =>
      api.post<CreativeUploadComplete>(
        `/creatives/${encoded(creativeId)}/upload-complete`,
        { body },
      ),
    onSuccess: (_result, variables) => {
      invalidate(qc, creativeById.key(variables.creativeId));
      invalidate(qc, CREATIVE_LIST_PREFIX);
    },
  });
}

export function useAbortCreativeUpload() {
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: ({ creativeId, assetId }: { creativeId: string; assetId: string }) =>
      api.post<void>(
        `/creatives/${encoded(creativeId)}/upload-abort`,
        { body: { asset_id: assetId } },
      ),
  });
}

export function useSaveCreativeContent() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['creatives'],
    mutationFn: ({ id, body }: { id: string; body: SaveCreativeContentRequest }) =>
      api.put<CreativeContentWriteResponse>(
        `/creatives/${encoded(id)}/content`,
        { body },
      ),
    onSuccess: ({ creative }) => {
      invalidate(qc, creativeById.key(creative.id));
      invalidate(qc, creativesList.all);
      invalidate(qc, creativeRevisions.key(creative.id));
      invalidate(qc, metaCreativePreviews.key(creative.id));
    },
  });
}

/**
 * The additions journal for one campaign. The poll lives on the resource, so
 * every reader of this key watches an accepted addition the same way.
 */
export function useCampaignCreativeAdditions(id: string) {
  return useQuery(campaignCreativeAdditions(id, { enabled: !!id }));
}

/**
 * What each creative of a campaign has delivered: one reporting read grouped
 * by creative for the totals, one by day and creative for a sparkline. The
 * window is the campaign's own; `null` before a campaign has published.
 */
export function useCampaignCreativeMetrics(
  campaignId: string,
  window: { from: string; to: string } | null,
) {
  const enabled = !!campaignId && window !== null;
  const request = (groupBy: ReportQueryRequest['group_by'], from: string): ReportQueryRequest => ({
    dataset: 'performance',
    attribution_scope: 'direct_only',
    date_from: from,
    date_to: window?.to ?? '1970-01-01',
    group_by: groupBy,
    filters: {
      assigned_production_ids: [],
      sold_production_ids: [],
      campaign_ids: [campaignId],
      audience_ids: [],
      audience_revision: null,
      creative_ids: [],
      creative_revision: null,
      channels: [],
      audience_types: [],
      targets: [],
      placements: [],
      currencies: ['USD'],
      provider_ad_ids: [],
      provider_creative_ids: [],
      attribution_effects: [],
      attribution_settings: [],
    },
    order: null,
    cursor: null,
    limit: 500,
  });
  // Both reads follow `next_cursor` to the end: a page is 500 rows, and four
  // creatives over 180 days by day and creative is 720 (audit, 2026-09-18).
  // The daily read is the sparkline's, which shows at most the last 30 days,
  // so it asks for only those; the totals keep the campaign's whole window.
  const totals = useQuery(reportingQueryAllPages(request(['creative'], window?.from ?? '1970-01-01'), { enabled }));
  const daily = useQuery(reportingQueryAllPages(
    request(['day', 'creative'], window ? metricsSparklineFrom(window) : '1970-01-01'),
    { enabled },
  ));
  return {
    totals: totals.data,
    daily: daily.data,
    isPending: enabled && (totals.isPending || daily.isPending),
    error: totals.error ?? daily.error,
    refetch: () => {
      void totals.refetch();
      void daily.refetch();
    },
  };
}

/**
 * The sparkline's reporting window: the last 30 days of the campaign's, or
 * all of a shorter one. The per-day read used to ask for the whole window and
 * take the first 500 rows, so a long campaign with several creatives lost its
 * newest days silently (audit, 2026-09-18).
 */
/**
 * Reports → Performance by channel for the channel tiles: the last 180 closed
 * New York days of direct sales, for the production picked or all the reader
 * may see. The same request the forecast's history makes, so the tiles and
 * the forecast quote one set of figures.
 */
export function useChannelBenchmarks(productionId: string | null) {
  const request = channelBenchmarkRequest(productionId, channelBenchmarkWindow());
  const query = useQuery(reportingQuery(request));
  return { benchmarks: channelBenchmarks(query.data), isPending: query.isPending, isError: query.isError };
}

export function metricsSparklineFrom(window: { from: string; to: string }): string {
  // Calendar days, not local-time arithmetic: the window is literal
  // YYYY-MM-DD keys and lib/datetime owns walking them.
  const days = calendarDayRange(window.from, window.to, 3_660);
  return days.slice(-30)[0] ?? window.from;
}

/**
 * Pause one creative slot of a published campaign (0030). The verdict is the
 * change row's; the plan and the sync status are refetched because the slot's
 * `paused_at` and the ads' statuses are what the card reads.
 */
export function useCampaignSlotPause() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({
      campaignId,
      position,
      expected_creative_revision,
    }: { campaignId: string; position: number; expected_creative_revision: number }) =>
      api
        .post<CreativeSlotChangeResponse>(
          `/campaigns/${encoded(campaignId)}/creatives/${position}/pause`,
          { body: { expected_creative_revision } },
        )
        .then((r) => r.change),
    onSuccess: (change) => {
      invalidate(qc, campaignPlan.key(change.campaign_id));
      invalidate(qc, campaignSyncStatus.key(change.campaign_id));
    },
  });
}

/** The exact reverse of [`useCampaignSlotPause`]. */
export function useCampaignSlotResume() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({
      campaignId,
      position,
      expected_creative_revision,
    }: { campaignId: string; position: number; expected_creative_revision: number }) =>
      api
        .post<CreativeSlotChangeResponse>(
          `/campaigns/${encoded(campaignId)}/creatives/${position}/resume`,
          { body: { expected_creative_revision } },
        )
        .then((r) => r.change),
    onSuccess: (change) => {
      invalidate(qc, campaignPlan.key(change.campaign_id));
      invalidate(qc, campaignSyncStatus.key(change.campaign_id));
    },
  });
}

export function useAddCampaignCreative() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, body }: { id: string; body: AddCampaignCreativeRequest }) =>
      api.post<CampaignCreativeAddition>(
        `/campaigns/${encoded(id)}/creative-additions`,
        { body },
      ),
    onSuccess: (row) => {
      invalidate(qc, campaignPlan.key(row.campaign_id));
      invalidate(qc, campaignCreativeAdditions.key(row.campaign_id));
    },
  });
}

export function useRetryCampaignCreativeAddition() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, operation }: { id: string; operation: string }) =>
      api.post<void>(`/campaigns/${encoded(id)}/creative-additions/${encoded(operation)}/retry`),
    onSuccess: (_result, { id }) => invalidate(qc, campaignCreativeAdditions.key(id)),
  });
}

/**
 * The operator abandoning an addition — the only verb that unwedges a campaign
 * whose addition will never succeed, and the reason a failed one does not
 * strand every other creative action.
 *
 * The server takes a `failed` or a `queued` row only (`running` still owns its
 * execution lease, `done` is the plan) and answers with the cancelled row. Two
 * keys are re-read: the journal it just wrote, and the campaign plan, because
 * cancelling also deletes the provider mirror rows the abandoned slot left
 * behind — its retry sibling changes the journal alone.
 */
export function useCancelCampaignCreativeAddition() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, operation }: { id: string; operation: string }) =>
      api.post<CampaignCreativeAddition>(
        `/campaigns/${encoded(id)}/creative-additions/${encoded(operation)}/cancel`,
      ),
    onSuccess: (_row, { id }) => {
      invalidate(qc, campaignCreativeAdditions.key(id));
      invalidate(qc, campaignPlan.key(id));
    },
  });
}

// ── Extend flight (campaign lifecycle 2b, 2026-10-21) ────────────────────

/** The campaign's extension journal and whether Extend is on for its provider. */
export function useCampaignFlightExtensions(id: string, enabled = true) {
  return useQuery(campaignFlightExtensions(id, { enabled: !!id && enabled }));
}

/**
 * Add one flight to a live or paused campaign. A Meta extension commits in the route and
 * answers with the plan carrying the new flight, which is cached at once so the campaign
 * page refreshes without a refetch; a TTD one is queued, and its plan moves when the
 * journal reads committed. Either way the journal is re-read, so its poll starts.
 */
export function useExtendCampaignFlight() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, body }: { id: string; body: ExtendCampaignFlightRequest }) =>
      api.post<ExtendCampaignFlightResponse>(
        `/campaigns/${encoded(id)}/flight-extensions`,
        { body },
      ),
    onSuccess: (response, { id }) => {
      if (response.plan) cacheCampaignPlan(qc, response.plan);
      else invalidate(qc, campaignPlan.key(id));
      invalidate(qc, campaignFlightExtensions.key(id));
      invalidate(qc, campaignSyncStatus.key(id));
    },
    // A lost answer or a refusal may still have left a row on the server (queued before the
    // answer was lost, or the open one the refusal names): re-read the journal so its Retry
    // and Cancel appear instead of a dialog that keeps refusing.
    onError: (_error, { id }) => invalidate(qc, campaignFlightExtensions.key(id)),
  });
}

/**
 * Re-queue a failed extension. The server keeps its hold and what it knows of the
 * provider create, so retrying a create it cannot confirm only looks for the flight.
 */
export function useRetryCampaignFlightExtension() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, extensionId }: { id: string; extensionId: string }) =>
      api.post<unknown>(
        `/campaigns/${encoded(id)}/flight-extensions/${encoded(extensionId)}/retry`,
      ),
    onSuccess: (_result, { id }) => invalidate(qc, campaignFlightExtensions.key(id)),
    // A lost answer may have landed: re-read, so the row shows what the server holds.
    onError: (_error, { id }) => invalidate(qc, campaignFlightExtensions.key(id)),
  });
}

/**
 * Abandon an extension that is not yet in the plan. Once the provider may have been
 * touched the server finishes the cancel only after it found and removed the flight, so
 * the row can stay open after this answers; the plan and the sync status are re-read
 * because a cancel releases the campaign's hold.
 */
export function useCancelCampaignFlightExtension() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['campaigns'],
    mutationFn: ({ id, extensionId }: { id: string; extensionId: string }) =>
      api.post<unknown>(
        `/campaigns/${encoded(id)}/flight-extensions/${encoded(extensionId)}/cancel`,
      ),
    onSuccess: (_result, { id }) => {
      invalidate(qc, campaignFlightExtensions.key(id));
      invalidate(qc, campaignPlan.key(id));
      invalidate(qc, campaignSyncStatus.key(id));
    },
    onError: (_error, { id }) => invalidate(qc, campaignFlightExtensions.key(id)),
  });
}
